/**
 * Static 1-D spatial index over x-intervals: answers "which items may intersect
 * [a, b]?" in time proportional to the answer. Items are bucketed into fixed-width
 * columns (compressed-row arrays, no per-query allocation). Results are candidates;
 * callers still test the exact geometry with their own strictness rules.
 */
export class XIndex {
  /** Candidate ids found by the last `query`, valid in `found[0 .. count)`. */
  readonly found: Int32Array;

  private readonly origin: number;
  private readonly cellSize: number;
  private readonly cellCount: number;
  /** cellStart[c] .. cellStart[c + 1] indexes into `entries` for column c. */
  private readonly cellStart: Int32Array;
  private readonly entries: Int32Array;
  /** Per-item stamp of the last query that reported it (removes duplicates). */
  private readonly seen: Uint32Array;
  private stamp = 0;

  constructor(x1: ArrayLike<number>, x2: ArrayLike<number>, cellSize = 2) {
    const count = x1.length;
    this.cellSize = cellSize;
    this.found = new Int32Array(count);
    this.seen = new Uint32Array(count);
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    for (let i = 0; i < count; i++) {
      min = Math.min(min, x1[i]!);
      max = Math.max(max, x2[i]!);
    }
    this.origin = count > 0 ? Math.floor(min / cellSize) * cellSize : 0;
    this.cellCount = count > 0 ? Math.floor((max - this.origin) / cellSize) + 1 : 0;
    this.cellStart = new Int32Array(this.cellCount + 1);
    // Counting pass, then fill pass (classic CSR construction).
    for (let i = 0; i < count; i++) {
      const c2 = this.cell(x2[i]!);
      for (let c = this.cell(x1[i]!); c <= c2; c++) this.cellStart[c + 1]!++;
    }
    for (let c = 0; c < this.cellCount; c++) this.cellStart[c + 1]! += this.cellStart[c]!;
    this.entries = new Int32Array(this.cellStart[this.cellCount]!);
    const fill = this.cellStart.slice(0, this.cellCount);
    for (let i = 0; i < count; i++) {
      const c2 = this.cell(x2[i]!);
      for (let c = this.cell(x1[i]!); c <= c2; c++) this.entries[fill[c]!++] = i;
    }
  }

  /**
   * Collects the ids of all items whose column range touches [a, b] into `found`
   * and returns how many there are. Ids come out in a deterministic order.
   */
  query(a: number, b: number): number {
    if (this.cellCount === 0 || b < a) return 0;
    const c1 = this.cell(a);
    const c2 = this.cell(b);
    this.stamp = (this.stamp + 1) >>> 0;
    if (this.stamp === 0) {
      // Wrapped around after 2^32 queries: forget old stamps.
      this.seen.fill(0);
      this.stamp = 1;
    }
    let n = 0;
    for (let c = c1; c <= c2; c++) {
      const end = this.cellStart[c + 1]!;
      for (let e = this.cellStart[c]!; e < end; e++) {
        const id = this.entries[e]!;
        if (this.seen[id] !== this.stamp) {
          this.seen[id] = this.stamp;
          this.found[n++] = id;
        }
      }
    }
    return n;
  }

  /** Column of coordinate x, clamped to the index (out-of-range queries see the edge columns). */
  private cell(x: number): number {
    const c = Math.floor((x - this.origin) / this.cellSize);
    return Math.min(Math.max(c, 0), this.cellCount - 1);
  }
}
